import { createPortal } from 'react-dom';
import Button from '@uk-source-web/button';
import {
  Grid,
  ModalContent,
  ModalHeader,
  ModalHeaderContent,
  ModalTitle,
  ModalSubtitle,
  ModalOverlay,
  Select,
  Buttons,
  InputDiv,
  ErrorMessage,
  Asterisk,
  CloseButton,
  UnderlinedText,
  InfoContainer,
  SectionDescription,
  TimelineContainer,
  TimelineStep,
  TimelineDot,
  MCAInfoText,
  TimelineLineExtended,
  SectionBoldTitle,
  TimelineDotAdditionalInformation,
} from './EditMicrosoftDataForm.styles';
import { MCAFormSection } from './MCAFormSection';
import Icon from '@legacy-vfuk/core-icon';
import { translatedMDFValues } from './translatedValues';
import { useEditMicrosoftDataForm, EditMicrosoftDataFormProps } from './useEditMicrosoftDataForm';
import { StandardInput, WideInput } from './FormComponents';
import { ConfigureBundleOptionSelected, useAppSelector } from '@/store';

const tenantPortingNameLink = 'https://admin.microsoft.com/Adminportal/Home#/Domains';
const OPCO = process.env.GATSBY_OPCO;

const EditMicrosoftDataForm = (props: EditMicrosoftDataFormProps) => {
  const { isOpen, onClose, needsMCA, needsAdditionalInfo, accountId, onMcaVerified } = props;

  const {
    counties,
    loading,
    formData,
    optionalData,
    errors,
    handleOptionalData,
    handleChange,
    handleSubmit,
    getInputRef,
    isTenantValid,
    isPrincipleNameValid,
    isEmailValid,
    isAltEmailValid,
    isFirstNameValid,
    isLastNameValid,
    isBusinessNameValid,
    isAddressValid,
    isZipCodeValid,
    isCityValid,
    isCountryValid,
    isStateValid,
    isPhoneValid,
    isOrgNumberValid,
  } = useEditMicrosoftDataForm(props);

  const { tenantPortingRequired, configureBundleOptionSelected } = useAppSelector(({ userBasket: { tenantPortingRequired, configureBundleOptionSelected } }) => ({
    tenantPortingRequired,
    configureBundleOptionSelected,
  }));

  if (!isOpen) return null;

  const tenantPortingRequiredTitleAndSubtitle = () => {
    if(tenantPortingRequired && configureBundleOptionSelected === ConfigureBundleOptionSelected.newMS) {
      return {title: translatedMDFValues.tenantPortingRequiredTitle, subtitle: translatedMDFValues.tenantPortingRequiredSubtitle};
    }
    return {title: translatedMDFValues.editMicrosoftModalTitle, subtitle: translatedMDFValues.editMicrosoftModalSubtitle};
  }

  return createPortal(
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalHeaderContent>
            <ModalTitle>{tenantPortingRequiredTitleAndSubtitle().title}</ModalTitle>
            <ModalSubtitle>{tenantPortingRequiredTitleAndSubtitle().subtitle}</ModalSubtitle>
          </ModalHeaderContent>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        {needsMCA && (
          <MCAInfoText>{translatedMDFValues.mcaInfoText}</MCAInfoText>
        )}
        <TimelineContainer needsMCA={needsMCA}>
          <TimelineStep>
            {needsMCA && <TimelineDot />}
            {needsMCA && <TimelineLineExtended />}
            {/* MCA Form Section with Timeline */}
            {needsMCA && <MCAFormSection accountId={accountId} needsMCA={needsMCA} onMcaVerified={onMcaVerified} />}
            {needsMCA && needsAdditionalInfo && <TimelineDotAdditionalInformation />}

            {/* Additional Information Section */}
            {(needsAdditionalInfo ?? true) && (
              <>
                <SectionBoldTitle>{translatedMDFValues.additionalInformation}</SectionBoldTitle>

                <SectionDescription>{translatedMDFValues.additionalInformationDescription}</SectionDescription>

                <Grid>
                  <StandardInput
                    label={translatedMDFValues.microsoftTenantName}
                    name="tenantName"
                    value={formData.tenantName}
                    placeholder={translatedMDFValues.tenantNamePlaceholder}
                    error={errors.tenantName}
                    isValid={isTenantValid}
                    errorMessage={translatedMDFValues.nameIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('tenantName')}
                  />

                  <StandardInput
                    label={translatedMDFValues.userPrincipleName}
                    name="username"
                    value={formData.username}
                    placeholder={translatedMDFValues.usernamePlaceholder}
                    error={errors.username}
                    isValid={isPrincipleNameValid}
                    errorMessage={translatedMDFValues.usernameIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('username')}
                  />

                  <>
                    <InfoContainer>
                      <Icon name="info" group="state" />
                      {translatedMDFValues.tenantNameInfo}
                    </InfoContainer>
                    <InfoContainer>
                      <UnderlinedText
                        onClick={() => window.open(tenantPortingNameLink, '_blank', 'noopener')}
                        rel="noopener noreferrer"
                      >
                        {translatedMDFValues.accessTenantName}
                      </UnderlinedText>
                    </InfoContainer>
                  </>

                  <StandardInput
                    label={translatedMDFValues.email}
                    name="email"
                    value={formData.email}
                    placeholder={translatedMDFValues.emailPlaceholder}
                    error={errors.email}
                    isValid={isEmailValid}
                    errorMessage={translatedMDFValues.emailInvalid}
                    onChange={handleChange}
                    inputRef={getInputRef('email')}
                  />

                  <StandardInput
                    label={translatedMDFValues.alternateEmail}
                    name="altEmail"
                    value={formData.altEmail}
                    placeholder={translatedMDFValues.alternateEmailPlaceholder}
                    error={errors.altEmail}
                    isValid={isAltEmailValid}
                    errorMessage={translatedMDFValues.altEmailIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('altEmail')}
                  />

                  <StandardInput
                    label={translatedMDFValues.firstName}
                    name="firstName"
                    value={formData.firstName}
                    placeholder={translatedMDFValues.firstNamePlaceholder}
                    error={errors.firstName}
                    isValid={isFirstNameValid}
                    errorMessage={translatedMDFValues.firstNameIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('firstName')}
                  />

                  <StandardInput
                    label={translatedMDFValues.lastName}
                    name="lastName"
                    value={formData.lastName}
                    placeholder={translatedMDFValues.lastNamePlaceholder}
                    error={errors.lastName}
                    isValid={isLastNameValid}
                    errorMessage={translatedMDFValues.lastNameIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('lastName')}
                  />

                  <WideInput
                    label={translatedMDFValues.businessName}
                    name="businessName"
                    value={formData.businessName}
                    placeholder={translatedMDFValues.businessNamePlaceholder}
                    error={errors.businessName}
                    isValid={isBusinessNameValid}
                    errorMessage={translatedMDFValues.businessNameIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('businessName')}
                  />

                  <WideInput
                    label={translatedMDFValues.address}
                    name="address"
                    value={formData.address}
                    placeholder={translatedMDFValues.addressPlaceholder}
                    error={errors.address}
                    isValid={isAddressValid}
                    errorMessage={translatedMDFValues.addressIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('address')}
                    useErrorInput={true}
                  />

                  <WideInput
                    label={translatedMDFValues.address2}
                    name="address2"
                    value={optionalData.address2}
                    placeholder={translatedMDFValues.address2Placeholder}
                    onChange={handleOptionalData}
                    inputRef={getInputRef('address2')}
                    required={false}
                    error={false}
                  />

                  <StandardInput
                    label={translatedMDFValues.zipCode}
                    name="zipCode"
                    value={formData.zipCode}
                    placeholder={translatedMDFValues.zipCodePlaceholder}
                    error={errors.zipCode}
                    isValid={isZipCodeValid}
                    errorMessage={translatedMDFValues.zipCodeIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('zipCode')}
                  />

                  <StandardInput
                    label={translatedMDFValues.city}
                    name="city"
                    value={formData.city}
                    placeholder={translatedMDFValues.cityPlaceholder}
                    error={errors.city}
                    isValid={isCityValid}
                    errorMessage={translatedMDFValues.cityIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('city')}
                  />

                  {/* Country */}
                  <InputDiv>
                    <label htmlFor="country">
                      {translatedMDFValues.country} <Asterisk>*</Asterisk>
                    </label>
                    <Select
                      id="country"
                      name="country"
                      value={formData.country}
                      style={{ border: errors.country || isCountryValid === false ? '1px solid red' : '' }}
                      onChange={handleChange}
                    >
                      <option value="">{translatedMDFValues.selectCountry}</option>
                      {OPCO === 'IE' && <option value="IRL">IRL</option>}
                      {OPCO === 'PT' && <option value="PRT">PRT</option>}
                    </Select>
                    {(errors.country || isCountryValid === false) && (
                      <ErrorMessage>{translatedMDFValues.chooseCountry}</ErrorMessage>
                    )}
                  </InputDiv>

                  {/* County */}
                  <InputDiv>
                    <label htmlFor="county">
                      {translatedMDFValues.county} <Asterisk>*</Asterisk>
                    </label>
                    {loading ? (
                      <div>{translatedMDFValues.loadingCounties}</div>
                    ) : (
                      <Select
                        id="county"
                        name="county"
                        onChange={handleChange}
                        style={{ border: errors.county || isStateValid === false ? '1px solid red' : '' }}
                        value={formData.county}
                      >
                        <option value="">{translatedMDFValues.selectCounty}</option>
                        {counties.map((county, index) => (
                          <option key={index} value={county}>
                            {county}
                          </option>
                        ))}
                      </Select>
                    )}
                    {(errors.county || isStateValid === false) && (
                      <ErrorMessage>{translatedMDFValues.chooseCounty}</ErrorMessage>
                    )}
                  </InputDiv>

                  <WideInput
                    label={translatedMDFValues.phoneNumber}
                    name="phone"
                    value={formData.phone}
                    placeholder={translatedMDFValues.phoneNumberPlaceholder}
                    error={errors.phone}
                    isValid={isPhoneValid}
                    errorMessage={translatedMDFValues.phoneInvalid}
                    onChange={handleChange}
                    inputRef={getInputRef('phone')}
                  />

                  <WideInput
                    label={translatedMDFValues.organizationRegistrationNumber}
                    name="registrationNumber"
                    value={formData.registrationNumber}
                    placeholder={translatedMDFValues.orgNumberPlaceholder}
                    error={errors.registrationNumber}
                    isValid={isOrgNumberValid}
                    errorMessage={translatedMDFValues.orgNumberIncorrect}
                    onChange={handleChange}
                    inputRef={getInputRef('registrationNumber')}
                  />

                  <StandardInput
                    label={translatedMDFValues.consentUserFirstName}
                    name="consentFirstName"
                    value={formData.consentFirstName}
                    placeholder={translatedMDFValues.consentFirstNamePlaceholder}
                    error={!!errors.consentFirstName}
                    errorMessage={translatedMDFValues.fillField}
                    onChange={handleChange}
                    inputRef={getInputRef('consentFirstName')}
                  />

                  <StandardInput
                    label={translatedMDFValues.consentUserLastName}
                    name="consentLastName"
                    value={formData.consentLastName}
                    placeholder={translatedMDFValues.consentLastNamePlaceholder}
                    error={!!errors.consentLastName}
                    errorMessage={translatedMDFValues.fillField}
                    onChange={handleChange}
                    inputRef={getInputRef('consentLastName')}
                  />

                  <WideInput
                    label={translatedMDFValues.consentUserEmail}
                    name="consentEmail"
                    value={formData.consentEmail}
                    placeholder={translatedMDFValues.consentEmailPlaceholder}
                    error={!!errors.consentEmail}
                    errorMessage={translatedMDFValues.fillField}
                    onChange={handleChange}
                    inputRef={getInputRef('consentEmail')}
                  />

                  <WideInput
                    label={translatedMDFValues.consentUserPhone}
                    name="consentPhone"
                    value={formData.consentPhone}
                    placeholder={translatedMDFValues.consentPhonePlaceholder}
                    error={!!errors.consentPhone}
                    errorMessage={translatedMDFValues.fillField}
                    onChange={handleChange}
                    inputRef={getInputRef('consentPhone')}
                  />
                </Grid>
              </>
            )}
          </TimelineStep>
        </TimelineContainer>
      </ModalContent>
      <Buttons>
        <Button text={translatedMDFValues.cancel} width="full" appearance="alt1" onClick={onClose} />
        <Button text={translatedMDFValues.saveDetails} width="full" appearance="primary" onClick={handleSubmit} />
      </Buttons>
    </ModalOverlay>,
    document.body
  ) as unknown as JSX.Element;
};

export default EditMicrosoftDataForm;
